// Tab strip at the top of the combat modal — one chip per queued fight,
// color-coded by the attacker's player. Clicking switches the modal to
// that fight; the parent owns the selected index.
//
// Spec: docs/02-systems/combat-visualisation.md §1a.

import { useT } from '../../i18n/index.js';
import type { CombatFightRef } from '../../render/combatRounds.js';

interface CombatFightHeaderProps {
  readonly fights: ReadonlyArray<CombatFightRef>;
  readonly selectedIndex: number;
  readonly attackerPlayerIndexByFight?: ReadonlyMap<string, number>;
  readonly onSelectFight: (index: number) => void;
}

function chipKey(f: CombatFightRef): string {
  return `${f.attackerId}::${f.defenderId}`;
}

function playerColor(index: number | undefined): string {
  if (index === undefined) return 'bg-muted text-muted-foreground';
  switch (index % 4) {
    case 0:
      return 'bg-amber-500/20 text-amber-800 dark:text-amber-200 border-amber-500/40';
    case 1:
      return 'bg-rose-500/20 text-rose-800 dark:text-rose-200 border-rose-500/40';
    case 2:
      return 'bg-sky-500/20 text-sky-800 dark:text-sky-200 border-sky-500/40';
    default:
      return 'bg-violet-500/20 text-violet-800 dark:text-violet-200 border-violet-500/40';
  }
}

function unitLabel(id: string): string {
  return `#${id.replace(/^unit_/, '')}`;
}

export function CombatFightHeader({
  fights,
  selectedIndex,
  attackerPlayerIndexByFight,
  onSelectFight,
}: CombatFightHeaderProps) {
  const t = useT();
  if (fights.length <= 1) {
    // Single fight — no tab strip needed; show a static header instead.
    const only = fights[0];
    return (
      <header className="flex items-baseline gap-2 border-b border-border/60 pb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
          {t('combat.title')}
        </span>
        {only ? (
          <span className="text-sm font-medium">
            {t('combat.vs', {
              attacker: unitLabel(only.attackerId),
              defender: unitLabel(only.defenderId),
            })}
          </span>
        ) : null}
      </header>
    );
  }

  return (
    <header className="flex flex-col gap-2 border-b border-border/60 pb-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
        {t('combat.titleCount', { count: fights.length })}
      </span>
      <div className="flex flex-wrap gap-1" role="tablist">
        {fights.map((f, idx) => {
          const key = chipKey(f);
          const playerIndex = attackerPlayerIndexByFight?.get(key);
          const selected = idx === selectedIndex;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelectFight(idx)}
              className={`rounded-md border px-2 py-1 text-xs transition ${
                selected
                  ? `${playerColor(playerIndex)} ring-1 ring-rose-300/40`
                  : `${playerColor(playerIndex)} opacity-60 hover:opacity-100`
              }`}
            >
              {`${unitLabel(f.attackerId)} ⟶ ${unitLabel(f.defenderId)}`}
            </button>
          );
        })}
      </div>
    </header>
  );
}
